import { Color3, Constants, MeshBuilder, type Mesh, type Scene, type ShaderMaterial, type Vector3 } from '@babylonjs/core';
import { registerGroundWarningShaders } from './ground-warning-shaders';
import type { VfxWarmup } from './vfx-warmup';
import { createVfxShaderMaterial } from './vfx-shader-material';
import { reconcileKeyedEffects } from './keyed-effects';

/** How a ground warning's crosshair spins through its windup. */
export type GroundWarningSpinTiming = 'increasing' | 'linear';

/** One enemy attack's ground warning this frame: where it lands, and how close it is to landing. */
export interface GroundWarningState {
    readonly id: string;
    /** Its center on the floor, in scene space. */
    readonly center: Vector3;
    readonly radiusMeters: number;
    /** 0 as it starts, 1 as it lands. */
    readonly progress: number;
    /** How long it takes from start to landing: the crosshair spins through it. */
    readonly durationMs: number;
    /** Whether the spin speeds up as its time runs out (the default) or turns steadily, as far in all. */
    readonly spinTiming?: GroundWarningSpinTiming;
    /** 0 to 1: the bright flash as it lands. */
    readonly lockFlash?: number;
    /** Something is falling onto it (a lob): its shadow grows at the center. */
    readonly shadow?: boolean;
}

/** The quad reaches this far out, in radii: room for the blades' outer points and the drop shadow. */
const OUTER_RADII = 1.3;
/** The edge ring's width, how far its edges soften and how wide its bevel is, in meters, whatever the warning's size. */
const EDGE_WIDTH_METERS = 0.03;
const FEATHER_METERS = 0.012;
const BEVEL_METERS = 0.025;
/** Just above the floor, under bodies. */
const FLOOR_OFFSET = 0.035;
/** Every ground warning's red; other marks that warn on the floor (`GroundLineSystem`) take it too. */
export const GROUND_WARNING_COLOR_HEX = '#e0412f';
const WARNING_COLOR = Color3.FromHexString(GROUND_WARNING_COLOR_HEX);
/** Its lock flash, brighter; `GroundLineSystem` flashes a charge lane in it too. */
export const GROUND_WARNING_FLASH_COLOR_HEX = '#ff7a5c';
const FLASH_COLOR = Color3.FromHexString(GROUND_WARNING_FLASH_COLOR_HEX);

interface DrawnWarning {
    readonly mesh: Mesh;
    readonly material: ShaderMaterial;
    /** The radius it was built for: its plane and edge widths are sized to it. */
    readonly radius: number;
}

/**
 * Draws where an enemy attack will land (docs/gameplay/combat-vfx.md,
 * "Ground warnings") as a bevelled crosshair inlaid on the floor: a thin
 * ring at the radius crossed by four spear blades pointing in at a center
 * diamond, a ring growing out from the center to meet the edge as it lands, a falling
 * thing's shadow for a lob, and a lock flash. Matte, with only a faint tint
 * inside, so the floor stays seen and overlapping warnings stay apart. Takes exactly the warnings shown
 * this frame.
 */
export class GroundWarningSystem {
    private readonly drawn = new Map<string, DrawnWarning>();
    private timeSeconds = 0;

    constructor(private readonly scene: Scene) {
        registerGroundWarningShaders();
    }

    warm(warmup: VfxWarmup): void {
        warmup.material(this.createMaterial('ground-warning-warmup'));
    }

    setWarnings(states: readonly GroundWarningState[]): void {
        reconcileKeyedEffects(this.drawn, states, {
            create: (state) => this.create(state),
            // Its plane and edge widths are sized to its radius.
            isStale: (warning, state) => Math.abs(warning.radius - resolveRadius(state)) > 1e-3,
            apply: ({ material, mesh }, state) => {
                mesh.position.set(state.center.x, state.center.y + FLOOR_OFFSET, state.center.z);
                material.setFloat('progress', Math.min(1, Math.max(0, state.progress)));
                material.setFloat('lockFlash', Math.min(1, Math.max(0, state.lockFlash ?? 0)));
                material.setFloat('shadow', state.shadow ? 1 : 0);
                material.setFloat('windupSeconds', Math.max(0, state.durationMs) / 1000);
                material.setFloat('spinRamp', state.spinTiming === 'linear' ? 0 : 1);
            },
            unseen: () => 'remove',
            discard: (warning) => {
                warning.material.dispose();
                warning.mesh.dispose();
            },
        });
    }

    update(deltaSeconds: number): void {
        this.timeSeconds += deltaSeconds;
        this.drawn.forEach((warning) => warning.material.setFloat('time', this.timeSeconds));
    }

    dispose(): void {
        this.setWarnings([]);
    }

    private create(state: GroundWarningState): DrawnWarning {
        const name = `ground-warning-${state.id}`;
        const radius = resolveRadius(state);
        const mesh = MeshBuilder.CreatePlane(name, { size: radius * 2 * OUTER_RADII }, this.scene);
        // A plane faces -Z; lay it on the floor.
        mesh.rotation.x = Math.PI / 2;
        mesh.isPickable = false;
        const material = this.createMaterial(name);
        material.setFloat('edgeWidth', EDGE_WIDTH_METERS / radius);
        material.setFloat('feather', FEATHER_METERS / radius);
        material.setFloat('bevel', BEVEL_METERS / radius);
        mesh.material = material;
        return { mesh, material, radius };
    }

    private createMaterial(name: string): ShaderMaterial {
        const material = createVfxShaderMaterial(this.scene, name, 'groundWarning', {
            uniforms: ['worldViewProjection', 'time', 'progress', 'lockFlash', 'shadow', 'outer', 'edgeWidth', 'feather', 'bevel', 'windupSeconds', 'spinRamp', 'warningColor', 'flashColor'],
        });
        // Matte: blended, never added.
        material.alphaMode = Constants.ALPHA_COMBINE;
        material.setFloat('outer', OUTER_RADII);
        material.setColor3('warningColor', WARNING_COLOR);
        material.setColor3('flashColor', FLASH_COLOR);
        material.setFloat('edgeWidth', 0.04);
        material.setFloat('feather', 0.01);
        material.setFloat('bevel', 0.02);
        return material;
    }
}

/** A warning's drawn radius, never so small its edges divide by nothing. */
function resolveRadius(state: GroundWarningState): number {
    return Math.max(0.05, state.radiusMeters);
}
